// Copyright (c) 2022 by Move Closer

import VueCompositionAPI from '@vue/composition-api'
import VueI18n from 'vue-i18n'
import VueRouter from 'vue-router'
import { AsyncComponent, PluginObject, VueConstructor } from 'vue'

import { CustomDSLConfiguration, DSL_CONFIG_INJECTION_KEY } from './contracts'
import { CustomCard } from './molecules/Card'

/**
 * List of all available DSL components.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
const componentsRegistry: Record<string, AsyncComponent | VueConstructor> = {
  // All components go here as followed:
  //    Name: ConstructorClass

  // Atoms:
  Accordion: () => import(/* webpackChunkName: "dsl/Accordion" */ './atoms/Accordion/Accordion.vue'),
  Alert: () => import(/* webpackChunkName: "dsl/Alert" */ './atoms/Alert/Alert.vue'),
  Badge: () => import(/* webpackChunkName: "dsl/Badge" */ './atoms/Badge/Badge.vue'),
  Button: () => import(/* webpackChunkName: "dsl/Button" */ './atoms/Button/Button.vue'),
  ButtonLink: () => import(/* webpackChunkName: "dsl/ButtonLink" */ './atoms/ButtonLink/ButtonLink.vue'),
  Check: () => import(/* webpackChunkName: "dsl/Check" */ './atoms/Check/Check.vue'),
  DrawerInner: () => import(/* webpackChunkName: "dsl/DrawerInner" */ './atoms/DrawerInner/DrawerInner.vue'),
  DateTimePicker: () => import(/* webpackChunkName: "dsl/DateTimePicker" */ './atoms/DateTimePicker/DateTimePicker.vue'),
  Heading: () => import(/* webpackChunkName: "dsl/Heading" */ './atoms/Heading/Heading.vue'),
  FileSelect: () => import(/* webpackChunkName: "dsl/FileSelect" */ './atoms/FileSelect/FileSelect.vue'),
  Icon: () => import(/* webpackChunkName: "dsl/Icon" */ './atoms/Icon/Icon.vue'),
  Image: () => import(/* webpackChunkName: "dsl/Image" */ './atoms/Image/Image.vue'),
  Input: () => import(/* webpackChunkName: "dsl/Input" */ './atoms/Input/Input.vue'),
  Link: () => import(/* webpackChunkName: "dsl/Link" */ './atoms/Link/Link.vue'),
  List: () => import(/* webpackChunkName: "dsl/List" */ './atoms/List/List.vue'),
  ModalInner: () => import(/* webpackChunkName: "dsl/ModalInner" */ './atoms/ModalInner/ModalInner.vue'),
  Paragraph: () => import(/* webpackChunkName: "dsl/Paragraph" */ './atoms/Paragraph/Paragraph.vue'),
  PasswordInput: () => import(/* webpackChunkName: "dsl/PasswordInput" */ './atoms/PasswordInput/PasswordInput.vue'),
  ProgressBar: () => import(/* webpackChunkName: "dsl/ProgressBar" */ './atoms/ProgressBar/ProgressBar.vue'),
  SearchInput: () => import(/* webpackChunkName: "dsl/SearchInput" */ './atoms/SearchInput/SearchInput.vue'),
  Steps: () => import(/* webpackChunkName: "dsl/Steps" */ './atoms/Steps/Steps.vue'),
  Textarea: () => import(/* webpackChunkName: "dsl/Textarea" */ './atoms/Textarea/Textarea.vue'),
  // PreEditable: () => import(/* webpackChunkName: "dsl/PreEditable" */ './atoms/PreEditable/PreEditable.vue'),
  Select: () => import(/* webpackChunkName: "dsl/Select" */ './atoms/Select/Select.vue'),
  // Molecules:
  ArtisticCarousel: () => import(/* webpackChunkName: "dsl/ArtisticCarousel" */ './molecules/ArtisticCarousel/ArtisticCarousel.vue'),
  AsyncCardCheck: () => import(/* webpackChunkName: "dsl/AsyncCardCheck" */ './molecules/AsyncCardCheck/AsyncCardCheck.vue'),
  Breadcrumbs: () => import(/* webpackChunkName: "dsl/Breadcrumbs" */ './molecules/Breadcrumbs/Breadcrumbs.vue'),
  // fixme: nie dziala gdy Card jest ladowany asynchronicznie - HP ma wieczny loader
  // Card: () => import(/* webpackChunkName: "dsl/Card" */ './molecules/Card/Card.vue'),
  Card: CustomCard,
  CardCheck: () => import(/* webpackChunkName: "dsl/CardCheck" */ './molecules/CardCheck/CardCheck.vue'),
  Carousel: () => import(/* webpackChunkName: "dsl/Carousel" */ './molecules/Carousel/Carousel.vue'),
  Container: () => import(/* webpackChunkName: "dsl/Container" */ './molecules/Container/Container.vue'),
  Drawer: () => import(/* webpackChunkName: "dsl/Drawer" */ './molecules/Drawer/Drawer.vue'),
  Dropdown: () => import(/* webpackChunkName: "dsl/Dropdown" */ './molecules/Dropdown/Dropdown.vue'),
  Modal: () => import(/* webpackChunkName: "dsl/Modal" */ './molecules/Modal/Modal.vue'),
  Nav: () => import(/* webpackChunkName: "dsl/Nav" */ './molecules/Nav/Nav.vue'),
  NavTabs: () => import(/* webpackChunkName: "dsl/NavTabs" */ './molecules/NavTabs/NavTabs.vue'),
  Pagination: () => import(/* webpackChunkName: "dsl/Pagination" */ './molecules/Pagination/Pagination.vue'),
  Popover: () => import(/* webpackChunkName: "dsl/Popover" */ './molecules/Popover'),
  StarsRate: () => import(/* webpackChunkName: "dsl/StarsRate" */ './molecules/StarsRate/StarsRate.vue'),
  Video: () => import(/* webpackChunkName: "dsl/Video" */ './molecules/Video/Video.vue')
}

/**
 * Registers the DSL in the app.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const CustomDSL: PluginObject<CustomDSLConfiguration> = {
  install (_Vue, configuration?: CustomDSLConfiguration) {
    if (typeof configuration === 'undefined') {
      configuration = {}
    }

    if (typeof configuration.prefix !== 'string' || configuration.prefix.length === 0) {
      configuration.prefix = 'Ui'
    }

    _Vue.mixin({
      provide: { [DSL_CONFIG_INJECTION_KEY]: configuration }
    })

    // Next, register additional plugins required for the DSL to work.
    _Vue.use(VueCompositionAPI)
    _Vue.use(VueI18n)
    _Vue.use(VueRouter)

    // Finally, register the components.
    for (const [name, component] of Object.entries(componentsRegistry)) {
      _Vue.component(`${configuration.prefix}${name}`, component)
    }
  }
}
